var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { observable, action, runInAction } from 'mobx';
import IntlRelativeFormat from 'intl-relativeformat';
import moment from 'moment-timezone';
import { TranslationsBase } from './translations/base';
import { TranslationsEN } from './translations/en';
import axios from 'axios';
import { FORMATS, logger } from '@lib/common';
export class IntlStore {
    s;
    i18n;
    rf;
    currencyFormatter;
    currencyFormatterUSD;
    numberFormatter;
    constructor(opts) {
        const isServer = typeof window === 'undefined';
        const locale = (() => {
            if (isServer)
                return 'en';
            if (navigator.languages)
                return navigator.languages[0] || 'en';
            return navigator.language || 'en';
        })();
        const lng = locale.split('-')[0];
        const tz = isServer ? 'UTC' : Intl.DateTimeFormat().resolvedOptions().timeZone;
        this.s = observable({
            lng: lng,
            locale: locale,
            tz: tz,
            currency: {
                symbol: '$',
                code: 'USD',
                precision: 2,
                step: '0.01',
            },
            formats: {
                date: 'DD/MM/YYYY',
                time: 'h:mm a',
            },
            ...(opts.initialState || {}),
        });
        this.initFormatters(this.s);
        const instance = i18n.createInstance();
        const base = opts.useReactModule ? instance.use(initReactI18next) : instance;
        base
            .init({
            lng: this.s.lng,
            fallbackLng: 'en',
            interpolation: {
                escapeValue: false,
                format: this.format,
            },
            resources: {
                en: {
                    translation: {
                        ...TranslationsBase,
                        ...TranslationsEN,
                    },
                },
            },
        })
            .then();
        this.i18n = base;
    }
    initFormatters = (state) => {
        if (state.tz) {
        }
        this.rf = new IntlRelativeFormat(state.locale);
        this.currencyFormatterUSD = new Intl.NumberFormat(this.s.locale, {
            style: 'currency',
            currency: 'USD',
        });
        this.numberFormatter = new Intl.NumberFormat(this.s.locale, {});
        this.currencyFormatter = new Intl.NumberFormat(state.locale, {
            style: 'currency',
            currency: state.currency.code,
        });
        i18n.format = this.format;
    };
    format = (value, format) => {
        const getFormats = () => {
            if (!this.s || !this.s.formats) {
                return {
                    date: 'DD/MM/YYYY',
                    time: 'h:mm a',
                    datetime: 'DD/MM/YYYY, h:mm a',
                };
            }
            return {
                date: this.s.formats.date,
                time: this.s.formats.time,
                datetime: `${this.s.formats.date}, ${this.s.formats.time}`,
            };
        };
        if (format === 'currency') {
            const formatter = this.currencyFormatter;
            if (this.s && this.s.lng == 'ar') {
                //force latin numbering sytem in arabic
                return new Intl.NumberFormat('ar-u-nu-latn', {
                    style: 'currency',
                    currency: this.s.currency.code,
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                }).format(value);
            }
            else if (formatter) {
                return formatter.format(value);
            }
            return value;
        }
        if (format === 'currencyUSD') {
            return this.currencyFormatterUSD.format(value);
        }
        if (format === 'number') {
            return this.numberFormatter.format(value);
        }
        const formats = getFormats();
        if (format === 'timeFromTime') {
            return this.momentFromFormat(value, FORMATS.moment.time).format(formats.time);
        }
        if (format === 'timeFromTimestamp') {
            return this.momentFromMillis(value).format(formats.time);
        }
        if (format === 'dateFromDate') {
            return this.momentFromFormat(value, FORMATS.moment.date).format(formats.date);
        }
        if (format === 'dateFromTimestamp') {
            return this.momentFromMillis(value).format(formats.date);
        }
        if (format === 'dateFriendlyFromTimestamp') {
            return this.momentFromMillis(value).format(FORMATS.moment.date_friendly);
        }
        if (format === 'datetimeFromTimestamp') {
            return this.momentFromMillis(value).format(formats.datetime);
        }
        if (format === 'datetimeFromDateTime') {
            return this.momentFromFormat(value, FORMATS.moment.datetime).format(formats.datetime);
        }
        if (format === 'relative') {
            const v = new Date(value);
            return this.rf ? this.rf.format(v) : new IntlRelativeFormat('en').format(v);
        }
        return value;
    };
    momentNow = () => {
        return moment.tz(this.s.tz).locale(this.s.lng);
    };
    momentFromFormat = (dt, format) => {
        return moment.tz(dt, format, this.s.tz).locale(this.s.lng);
    };
    momentFromMillis = (millis) => {
        return moment.tz(millis, this.s.tz).locale(this.s.lng);
    };
    changeLanguage = async (lng) => {
        try {
            if (!this.i18n.hasResourceBundle(lng, 'translation')) {
                const apiUrl = process.env.NODE_ENV === 'production' ? 'https://api.cloudwaitress.com' : 'http://localhost:3010';
                const res = await axios(`${apiUrl}/intl/translation?language=${lng}`);
                this.i18n.addResourceBundle(lng, 'translation', res.data, true, true);
            }
            runInAction(() => {
                this.s.lng = lng;
                this.i18n.changeLanguage(lng);
            });
        }
        catch (e) {
            logger.captureException(e);
        }
    };
    set = (data) => {
        this.s = data;
        this.initFormatters(this.s);
    };
    update = (data) => {
        for (const key in data) {
            if (data.hasOwnProperty(key)) {
                const value = data[key];
                if (value !== undefined) {
                    // @ts-ignore
                    this.s[key] = value;
                }
            }
        }
        this.initFormatters(this.s);
    };
}
__decorate([
    observable
], IntlStore.prototype, "s", void 0);
__decorate([
    action
], IntlStore.prototype, "set", void 0);
__decorate([
    action
], IntlStore.prototype, "update", void 0);
//# sourceMappingURL=store.js.map